import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiArrowLeft, FiUser, FiFileText, FiMessageSquare, FiClock, FiCheckCircle,
  FiPlayCircle, FiPhone, FiMail, FiDownload, FiPaperclip, FiSend, FiMoreVertical
} = FiIcons;

// Mock data for a single referral
const referralData = {
  id: 'R001',
  patient: {
    name: 'Alice Johnson',
    age: 45,
    gender: 'Female',
  },
  status: 'In Progress',
  referringProvider: {
    name: 'Dr. Smith',
    specialty: 'General Dentist',
    avatar: 'https://i.pravatar.cc/150?u=drsmith',
    phone: '(555) 111-2222',
    email: 'dr.smith@example.com'
  },
  receivingProvider: {
    name: 'Dr. Davis',
    specialty: 'Oral Surgeon',
    avatar: 'https://i.pravatar.cc/150?u=drdavis',
    phone: '(555) 333-4444',
    email: 'dr.davis@example.com'
  },
  timeline: [
    { name: 'Referral Sent', status: 'completed', date: '2023-10-26' },
    { name: 'Referral Viewed', status: 'completed', date: '2023-10-26' },
    { name: 'Patient Contacted', status: 'completed', date: '2023-10-27' },
    { name: 'Consultation Scheduled', status: 'active', date: '2023-11-05' },
    { name: 'Treatment', status: 'upcoming', date: null },
    { name: 'Post-Op', status: 'upcoming', date: null },
  ],
  documents: [
    { name: 'Initial X-Rays.jpg', size: '2.3 MB', type: 'image' },
    { name: 'Patient Referral Form.pdf', size: '1.1 MB', type: 'pdf' },
  ],
  activity: [
    { user: 'Dr. Smith', action: 'sent the referral.', timestamp: 'Oct 26, 2023, 09:15 AM' },
    { user: 'System', action: 'notified Dr. Davis.', timestamp: 'Oct 26, 2023, 09:16 AM' },
    { user: 'Dr. Davis', action: 'viewed the referral.', timestamp: 'Oct 26, 2023, 11:30 AM' },
    { user: 'Receptionist (Dr. Davis\'s office)', action: 'called the patient.', timestamp: 'Oct 27, 2023, 10:00 AM' },
    { user: 'Receptionist (Dr. Davis\'s office)', action: 'scheduled consultation for Nov 5, 2023.', timestamp: 'Oct 27, 2023, 10:05 AM' },
  ]
};

const TimelineStep = ({ step, isLast }) => {
  const statusConfig = {
    completed: { icon: FiCheckCircle, color: 'text-green-400', line: 'bg-green-400' },
    active: { icon: FiPlayCircle, color: 'text-brand-secondary', line: 'bg-brand-secondary' },
    upcoming: { icon: FiClock, color: 'text-gray-500', line: 'bg-gray-600' },
  };
  const config = statusConfig[step.status];

  return (
    <div className="flex">
      <div className="flex flex-col items-center mr-4">
        <div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ring-4 ring-gray-800 ${config.color}`}>
            <SafeIcon icon={config.icon} className="w-5 h-5" />
          </div>
        </div>
        {!isLast && <div className={`w-px h-full ${config.line}`}></div>}
      </div>
      <div>
        <h4 className="font-semibold text-gray-200">{step.name}</h4>
        <p className="text-sm text-gray-400">{step.date || 'Pending'}</p>
      </div>
    </div>
  );
};

const ReferralTracking = () => {
  const { id } = useParams();
  // In a real app, you'd fetch referralData based on the id

  const { patient, status, timeline, referringProvider, receivingProvider, documents, activity } = referralData;

  const statusColors = {
    'Pending': 'bg-yellow-500/20 text-yellow-300',
    'In Progress': 'bg-blue-500/20 text-blue-300',
    'Completed': 'bg-green-500/20 text-green-300',
    'Declined': 'bg-red-500/20 text-red-300',
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-900 min-h-screen text-white">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/referrals" className="flex items-center text-sm text-brand-secondary hover:underline mb-4">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to All Referrals
          </Link>
          <div className="flex flex-col md:flex-row justify-between md:items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">Referral: {patient.name}</h1>
              <p className="text-gray-400">Tracking ID: {id}</p>
            </div>
            <div className="flex items-center mt-4 md:mt-0 space-x-4">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusColors[status]}`}>
                {status}
              </span>
              <button className="p-2 bg-gray-800 rounded-full hover:bg-gray-700">
                <SafeIcon icon={FiMoreVertical} />
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Timeline */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-1 bg-gray-800 p-6 rounded-xl"
          >
            <h3 className="text-xl font-bold mb-6">Referral Timeline</h3>
            <div className="space-y-6">
              {timeline.map((step, index) => (
                <TimelineStep key={step.name} step={step} isLast={index === timeline.length - 1} />
              ))}
            </div>
          </motion.div>

          {/* Right Column: Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Providers */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <h3 className="text-xl font-bold mb-4">Involved Parties</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[referringProvider, receivingProvider].map((provider, i) => (
                  <div key={i} className="bg-gray-800 p-5 rounded-lg">
                    <p className="text-sm font-semibold text-gray-400 mb-3">{i === 0 ? 'Referring Provider' : 'Receiving Provider'}</p>
                    <div className="flex items-center space-x-4">
                      <img src={provider.avatar} alt={provider.name} className="w-12 h-12 rounded-full" />
                      <div>
                        <p className="font-bold text-gray-100">{provider.name}</p>
                        <p className="text-sm text-gray-400">{provider.specialty}</p>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 text-sm">
                      <a href={`tel:${provider.phone}`} className="flex items-center text-gray-300 hover:text-brand-secondary">
                        <SafeIcon icon={FiPhone} className="mr-2" /> {provider.phone}
                      </a>
                      <a href={`mailto:${provider.email}`} className="flex items-center text-gray-300 hover:text-brand-secondary">
                        <SafeIcon icon={FiMail} className="mr-2" /> {provider.email}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Documents */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-gray-800 p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-4">Associated Documents</h3>
              <ul className="space-y-3">
                {documents.map(doc => (
                  <li key={doc.name} className="flex justify-between items-center bg-gray-700 p-3 rounded-lg">
                    <div className="flex items-center">
                      <SafeIcon icon={FiFileText} className="text-brand-secondary mr-3" />
                      <div>
                        <p className="font-medium text-gray-200">{doc.name}</p>
                        <p className="text-xs text-gray-400">{doc.size}</p>
                      </div>
                    </div>
                    <button className="p-2 rounded-full hover:bg-gray-600">
                      <SafeIcon icon={FiDownload} />
                    </button>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Activity & Communication */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-gray-800 p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-4">Activity & Communication</h3>
              <div className="space-y-4 max-h-64 overflow-y-auto pr-2">
                {activity.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-8 h-8 bg-gray-700 rounded-full flex-shrink-0 flex items-center justify-center mr-3">
                      <SafeIcon icon={FiUser} className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-300">
                        <span className="font-semibold text-gray-100">{item.user}</span> {item.action}
                      </p>
                      <p className="text-xs text-gray-500">{item.timestamp}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-700 flex items-center">
                <input
                  type="text"
                  placeholder="Send a secure message..."
                  className="flex-grow bg-gray-700 rounded-l-lg py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-brand-secondary"
                />
                <button className="p-2 bg-gray-700 hover:bg-gray-600">
                  <SafeIcon icon={FiPaperclip} />
                </button>
                <button className="bg-brand-secondary text-white font-bold py-2 px-4 rounded-r-lg hover:bg-opacity-80 transition-colors">
                  <SafeIcon icon={FiSend} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralTracking;